import { useState, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface UploadedImage {
  id: string;
  url: string;
  title: string;
  artist: string;
  year: string;
  wing: string;
  file?: File;
  preview?: string;
}

interface ImageUploadManagerProps {
  onSave: () => void;
  onClose: () => void;
  existingCount: number;
  currentWing?: 'north' | 'east' | 'south' | 'west' | null;
}

type Tab = 'url' | 'file';

const WING_OPTIONS = [
  { value: 'Main Gallery', label: 'Main Gallery' },
  { value: 'North Wing', label: 'North Wing' },
  { value: 'East Wing', label: 'East Wing' },
  { value: 'South Wing', label: 'South Wing' },
  { value: 'West Wing', label: 'West Wing' },
];

export default function ImageUploadManager({ onSave, onClose, existingCount, currentWing }: ImageUploadManagerProps) {
  const getDefaultWing = () => {
    if (!currentWing) return 'Main Gallery';
    const wingMap = {
      north: 'North Wing',
      east: 'East Wing',
      south: 'South Wing',
      west: 'West Wing'
    };
    return wingMap[currentWing];
  };

  const [activeTab, setActiveTab] = useState<Tab>('file');
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState('');
  const [urlPreview, setUrlPreview] = useState('');
  const [urlTitle, setUrlTitle] = useState('');
  const [urlArtist, setUrlArtist] = useState('Joshua Smith');
  const [urlYear, setUrlYear] = useState(new Date().getFullYear().toString());
  const [urlWing, setUrlWing] = useState(getDefaultWing());
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [uploadProgress, setUploadProgress] = useState<Record<string, 'pending' | 'uploading' | 'done' | 'error'>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter(f => f.type.startsWith('image/'));
    imageFiles.forEach((file, idx) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;
        const newImage: UploadedImage = {
          id: `upload-${Date.now()}-${idx}`,
          url: preview,
          preview,
          title: file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '),
          artist: 'Joshua Smith',
          year: new Date().getFullYear().toString(),
          wing: getDefaultWing(),
          file,
        };
        setUploadedImages(prev => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    });
  }, [currentWing]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const updateImage = (id: string, field: keyof UploadedImage, value: string) => {
    setUploadedImages(prev =>
      prev.map(img => img.id === id ? { ...img, [field]: value } : img)
    );
  };

  const removeImage = (id: string) => {
    setUploadedImages(prev => prev.filter(img => img.id !== id));
  };

  const handleUrlPreview = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) { setUrlError('Please enter a URL.'); return; }
    if (!/^https?:\/\/.+/.test(trimmed)) { setUrlError('URL must start with http:// or https://'); return; }
    setUrlError('');
    setUrlPreview(trimmed);
  };

  const handleAddUrl = () => {
    if (!urlPreview) { setUrlError('Please preview the image first.'); return; }
    const newImage: UploadedImage = {
      id: `url-${Date.now()}`,
      url: urlPreview,
      preview: urlPreview,
      title: urlTitle || 'Untitled',
      artist: urlArtist || 'Joshua Smith',
      year: urlYear || new Date().getFullYear().toString(),
      wing: urlWing,
    };
    setUploadedImages(prev => [...prev, newImage]);
    setUrlInput('');
    setUrlPreview('');
    setUrlTitle('');
    setUrlArtist('Joshua Smith');
    setUrlYear(new Date().getFullYear().toString());
    setUrlWing(getDefaultWing());
    setUrlError('');
  };

  const handleSave = async () => {
    if (uploadedImages.length === 0) return;
    setIsSaving(true);
    setSaveError('');

    const newProgress: Record<string, 'pending' | 'uploading' | 'done' | 'error'> = {};
    uploadedImages.forEach(img => { newProgress[img.id] = 'pending'; });
    setUploadProgress(newProgress);

    let anyError = false;

    for (const img of uploadedImages) {
      setUploadProgress(prev => ({ ...prev, [img.id]: 'uploading' }));
      try {
        let finalUrl = img.url;

        if (img.file) {
          const ext = img.file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
          const safeName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

          // Use the File object directly — most reliable method
          const { error: storageError } = await supabase.storage
            .from('artworks')
            .upload(safeName, img.file, {
              contentType: img.file.type || 'image/jpeg',
              upsert: false,
            });

          if (storageError) {
            console.error(`Storage error for "${img.title}":`, storageError.message);
            throw new Error(storageError.message);
          }

          const { data: publicData } = supabase.storage.from('artworks').getPublicUrl(safeName);
          finalUrl = publicData.publicUrl;
        }

        const { error: dbError } = await supabase.from('artworks').insert({
          title: img.title,
          artist: img.artist,
          year: img.year,
          medium: 'Oil on canvas',
          dimensions: 'Dimensions variable',
          image_url: finalUrl,
          description: `An original work titled "${img.title}" by ${img.artist}.`,
          location: img.wing,
        });

        if (dbError) {
          console.error(`Database error for "${img.title}":`, dbError.message);
          throw new Error(dbError.message);
        }

        setUploadProgress(prev => ({ ...prev, [img.id]: 'done' }));
      } catch (err: any) {
        console.error('Upload error:', err);
        setUploadProgress(prev => ({ ...prev, [img.id]: 'error' }));
        anyError = true;
      }
    }

    setIsSaving(false);

    if (!anyError) {
      onSave();
      onClose();
    } else {
      const failedCount = Object.values(uploadProgress).filter(s => s === 'error').length;
      setSaveError(`${failedCount || 'Some'} artwork(s) failed to upload. Check that your files are standard image formats (JPG, PNG, WEBP) and try again.`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-6 max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-stone-100">
          <div>
            <h2 className="text-2xl font-light text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Add Artworks to Gallery
            </h2>
            <p className="text-sm text-stone-500 font-light mt-1">
              Upload image files or paste a URL — all saved permanently to your gallery
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center bg-stone-100 text-stone-600 rounded-full hover:bg-stone-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-8 pt-5 pb-0">
          <button
            onClick={() => setActiveTab('file')}
            className={`px-5 py-2 rounded-t-lg text-sm font-light tracking-wide transition-all cursor-pointer whitespace-nowrap border-b-2 ${
              activeTab === 'file'
                ? 'border-stone-800 text-stone-800 bg-stone-50'
                : 'border-transparent text-stone-400 hover:text-stone-600'
            }`}
          >
            <i className="ri-upload-2-line mr-2"></i>Upload File
            <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Permanent</span>
          </button>
          <button
            onClick={() => setActiveTab('url')}
            className={`px-5 py-2 rounded-t-lg text-sm font-light tracking-wide transition-all cursor-pointer whitespace-nowrap border-b-2 ${
              activeTab === 'url'
                ? 'border-stone-800 text-stone-800 bg-stone-50'
                : 'border-transparent text-stone-400 hover:text-stone-600'
            }`}
          >
            <i className="ri-links-line mr-2"></i>Paste Image URL
            <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Permanent</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">

          {/* File Upload Tab */}
          {activeTab === 'file' && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 text-sm text-emerald-800 font-light flex gap-3 items-start">
                <i className="ri-cloud-line text-lg mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Permanent storage enabled.</strong> Your uploaded images are saved to the cloud and will always appear in your gallery — even after refreshing.
                </span>
              </div>
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all duration-300 ${
                  isDragging ? 'border-stone-600 bg-stone-50 scale-[1.01]' : 'border-stone-300 hover:border-stone-500 hover:bg-stone-50'
                }`}
              >
                <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-stone-100 rounded-full">
                  <i className="ri-image-add-line text-3xl text-stone-500"></i>
                </div>
                <p className="text-stone-700 font-light text-lg mb-1">Drop images here or <span className="underline">browse files</span></p>
                <p className="text-stone-400 text-sm font-light">Supports JPG, PNG, WEBP — select as many as you like</p>
              </div>
            </div>
          )}

          {/* URL Tab */}
          {activeTab === 'url' && (
            <div className="space-y-5">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 text-sm text-emerald-800 font-light flex gap-3 items-start">
                <i className="ri-information-line text-lg mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Tip:</strong> Pasting a URL links directly to the image and saves it permanently to your gallery database.
                </span>
              </div>

              <div className="space-y-3">
                <label className="text-xs text-stone-500 font-light block">Image URL</label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={urlInput}
                    onChange={e => { setUrlInput(e.target.value); setUrlError(''); }}
                    placeholder="https://example.com/painting.jpg"
                    className="flex-1 border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-800 font-light focus:outline-none focus:border-stone-500"
                  />
                  <button
                    onClick={handleUrlPreview}
                    className="px-5 py-2.5 bg-stone-100 text-stone-700 text-sm rounded-lg hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Preview
                  </button>
                </div>
                {urlError && <p className="text-xs text-red-500">{urlError}</p>}
              </div>

              {urlPreview && (
                <div className="flex gap-6 bg-stone-50 rounded-xl p-5 items-start">
                  <div className="w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-stone-100 flex items-center justify-center">
                    <img src={urlPreview} alt="Preview" className="max-w-full max-h-full object-contain" onError={() => { setUrlError('Could not load image from this URL.'); setUrlPreview(''); }} />
                  </div>
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="text-xs text-stone-500 font-light block mb-1">Title</label>
                      <input type="text" value={urlTitle} onChange={e => setUrlTitle(e.target.value)} placeholder="Untitled" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 font-light focus:outline-none focus:border-stone-500" />
                    </div>
                    <div>
                      <label className="text-xs text-stone-500 font-light block mb-1">Artist</label>
                      <input type="text" value={urlArtist} onChange={e => setUrlArtist(e.target.value)} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 font-light focus:outline-none focus:border-stone-500" />
                    </div>
                    <div>
                      <label className="text-xs text-stone-500 font-light block mb-1">Year</label>
                      <input type="text" value={urlYear} onChange={e => setUrlYear(e.target.value)} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 font-light focus:outline-none focus:border-stone-500" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-stone-500 font-light block mb-1">Wing Location</label>
                      <select
                        value={urlWing}
                        onChange={e => setUrlWing(e.target.value)}
                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 font-light focus:outline-none focus:border-stone-500 cursor-pointer"
                      >
                        {WING_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <button onClick={handleAddUrl} className="px-6 py-2 bg-stone-800 text-white text-sm rounded-lg hover:bg-stone-900 transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-add-line mr-1"></i> Add to List
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Queued Images */}
          {uploadedImages.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm text-stone-500 font-light">
                {uploadedImages.length} artwork{uploadedImages.length > 1 ? 's' : ''} ready to save
                {existingCount > 0 && ` · ${existingCount} already in gallery`}
              </p>
              {uploadedImages.map((img) => {
                const status = uploadProgress[img.id];
                return (
                  <div key={img.id} className="flex gap-5 bg-stone-50 rounded-xl p-4 items-start group">
                    <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-stone-100 relative flex items-center justify-center">
                      <img src={img.preview} alt={img.title} className="max-w-full max-h-full object-contain" />
                      {status === 'uploading' && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <i className="ri-loader-4-line text-white text-xl animate-spin"></i>
                        </div>
                      )}
                      {status === 'done' && (
                        <div className="absolute inset-0 bg-emerald-500/40 flex items-center justify-center">
                          <i className="ri-check-line text-white text-xl"></i>
                        </div>
                      )}
                      {status === 'error' && (
                        <div className="absolute inset-0 bg-red-500/40 flex items-center justify-center">
                          <i className="ri-close-line text-white text-xl"></i>
                        </div>
                      )}
                    </div>
                    {editingId === img.id ? (
                      <div className="flex-1 grid grid-cols-3 gap-3">
                        <div className="col-span-3">
                          <label className="text-xs text-stone-500 font-light block mb-1">Title</label>
                          <input type="text" value={img.title} onChange={e => updateImage(img.id, 'title', e.target.value)} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 font-light focus:outline-none focus:border-stone-500" />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-stone-500 font-light block mb-1">Artist</label>
                          <input type="text" value={img.artist} onChange={e => updateImage(img.id, 'artist', e.target.value)} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 font-light focus:outline-none focus:border-stone-500" />
                        </div>
                        <div>
                          <label className="text-xs text-stone-500 font-light block mb-1">Year</label>
                          <input type="text" value={img.year} onChange={e => updateImage(img.id, 'year', e.target.value)} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 font-light focus:outline-none focus:border-stone-500" />
                        </div>
                        <div className="col-span-3">
                          <label className="text-xs text-stone-500 font-light block mb-1">Wing Location</label>
                          <select
                            value={img.wing}
                            onChange={e => updateImage(img.id, 'wing', e.target.value)}
                            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 font-light focus:outline-none focus:border-stone-500 cursor-pointer"
                          >
                            {WING_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-3 flex justify-end">
                          <button onClick={() => setEditingId(null)} className="px-4 py-1.5 bg-stone-800 text-white text-xs rounded-lg hover:bg-stone-900 transition-colors cursor-pointer whitespace-nowrap">Done</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1">
                        <p className="text-base text-stone-800 font-light italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{img.title}</p>
                        <p className="text-sm text-stone-500 font-light">{img.artist} · {img.year}</p>
                        <p className="text-xs text-stone-600 font-light mt-1 flex items-center gap-1.5">
                          <i className="ri-map-pin-line text-xs"></i>
                          {img.wing}
                        </p>
                        {img.file && <p className="text-xs text-stone-400 font-light mt-0.5">{img.file.name}</p>}
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => setEditingId(img.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-stone-200 text-stone-600 text-xs rounded-lg hover:bg-stone-100 transition-colors cursor-pointer whitespace-nowrap">
                            <i className="ri-edit-line text-xs"></i> Edit details
                          </button>
                        </div>
                      </div>
                    )}
                    <button onClick={() => removeImage(img.id)} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors cursor-pointer opacity-0 group-hover:opacity-100">
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {saveError && (
            <p className="text-sm text-red-500 font-light text-center">{saveError}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-stone-100 flex items-center justify-between bg-white">
          <button onClick={onClose} className="px-6 py-2.5 text-stone-600 text-sm font-light rounded-lg hover:bg-stone-100 transition-colors cursor-pointer whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={uploadedImages.length === 0 || isSaving}
            className={`px-8 py-2.5 text-sm font-light tracking-wide rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              uploadedImages.length > 0 && !isSaving ? 'bg-stone-800 text-white hover:bg-stone-900 shadow-md' : 'bg-stone-200 text-stone-400 cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <><i className="ri-loader-4-line animate-spin"></i> Saving to gallery...</>
            ) : (
              <><i className="ri-cloud-upload-line"></i> Save {uploadedImages.length > 0 ? `${uploadedImages.length} ` : ''}to Gallery</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
